import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import BasicCRUD from './../core/basic-crud';
import { socketUseEffect, socket } from "../../socket";
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";
import resolveUserRole from "../utils/resolveUserRole";

// ---------- i18n ----------
const plural = (n, { one, few, many }) => {
    const n10 = n % 10, n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return one;
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
    return many;
};

const dict = {
    en: {
        // page
        title: "Certificates",
        subTitle: "add / edit certificates",
        homeURL: "/dashboard",
        addTitle: "Add Certificate",
        editTitle: "Edit Certificate",

        // columns
        colStatus: "Status",
        colDomainName: "Domain Name",
        colIssuedBy: "Issued By",
        colContactGroup: "Contact Group",

        // cells
        unknown: "Unknown",
        daysLeftLabel: "Days Left:",
        expired: "Expired",
        invalidDate: "Invalid Date",

        // issuer block
        dateRange: (from, to) => `${from} → ${to}`, // MM/DD/YYYY → MM/DD/YYYY
        notAvailable: " - ",

        // form tabs + fields
        tabMain: "Main",
        fieldDomainName: "Domain Name",
        fieldContactGroup: "Contact Group",
        basicAuth: "Basic Authorization",
        login: "Login",
        password: "Password",

        choose: " -- choose -- ",
        yes: "Yes",
        no: "No",

        // actions
        edit: "Edit",
        delete: "Delete",

        // quota/plan
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",
        memberViewOnly: "Members can only view certificates.",

        // delete dialog
        delAlert: "Deleting a certificate cannot be undone.",
        delText: "To confirm deletion, enter the name of the certificate in the next input field",
        delTitle: 'Delete certificate: "{name}"',

        // toasts
        toastEntity: "Certificate",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",

        // plural helpers
        dayLbl: (n) => `day${n === 1 ? "" : "s"}`,
        hourLbl: (n) => `hour${n === 1 ? "" : "s"}`,
    },

    ru: {
        title: "Сертификаты",
        subTitle: "добавление / редактирование сертификатов",
        homeURL: "/dashboard",
        addTitle: "Добавить сертификат",
        editTitle: "Редактировать сертификат",

        colStatus: "Статус",
        colDomainName: "Имя домена",
        colIssuedBy: "Кем выдан",
        colContactGroup: "Группа контактов",

        unknown: "Неизвестно",
        daysLeftLabel: "Осталось дней:",
        expired: "Истёк",
        invalidDate: "Неверная дата",

        dateRange: (from, to) => `${from} → ${to}`,
        notAvailable: " - ",

        tabMain: "Основное",
        fieldDomainName: "Имя домена",
        fieldContactGroup: "Группа контактов",
        basicAuth: "Базовая авторизация",
        login: "Логин",
        password: "Пароль",

        choose: " -- выберите -- ",
        yes: "Да",
        no: "Нет",

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",
        memberViewOnly: "У участников нет прав управлять сертификатами.",

        delAlert: "Удаление сертификата необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя сертификата в следующее поле ввода",
        delTitle: 'Удалить сертификат: «{name}»',

        toastEntity: "Сертификат",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",

        dayLbl: (n) => plural(n, { one: "день", few: "дня", many: "дней" }),
        hourLbl: (n) => plural(n, { one: "час", few: "часа", many: "часов" }),
    },

    ua: {
        title: "Сертифікати",
        subTitle: "додавання / редагування сертифікатів",
        homeURL: "/dashboard",
        addTitle: "Додати сертифікат",
        editTitle: "Редагувати сертифікат",

        colStatus: "Статус",
        colDomainName: "Назва домену",
        colIssuedBy: "Ким видано",
        colContactGroup: "Група контактів",

        unknown: "Невідомо",
        daysLeftLabel: "Залишилось днів:",
        expired: "Строк минув",
        invalidDate: "Невірна дата",

        dateRange: (from, to) => `${from} → ${to}`,
        notAvailable: " - ",

        tabMain: "Основне",
        fieldDomainName: "Назва домену",
        fieldContactGroup: "Група контактів",
        basicAuth: "Базова авторизація",
        login: "Логін",
        password: "Пароль",

        choose: " -- оберіть -- ",
        yes: "Так",
        no: "Ні",

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",
        memberViewOnly: "У учасників немає прав керувати сертифікатами.",

        delAlert: "Видалення сертифіката неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву сертифіката у наступне поле вводу",
        delTitle: 'Видалити сертифікат: «{name}»',

        toastEntity: "Сертифікат",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",

        dayLbl: (n) => plural(n, { one: "день", few: "дні", many: "днів" }),
        hourLbl: (n) => plural(n, { one: "година", few: "години", many: "годин" }),
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- component ----------
const Certificates = ({ lang: langProp, ...props }) => {
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "certificates";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});

    const [totalData, setTotalData] = useState([]);
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);
    const [afterDeletePath, setAfterDeletePath] = useState("/certificates");

    const { user } = useAuth();
    const currentUserRole = useMemo(() => resolveUserRole(user), [user]);
    const canManageCertificates = currentUserRole !== "member";
    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]);

    // After successful deletion in single view, compute next redirect target
    useEffect(() => {
        if (!(props.singleArgs?.delete) || response?.delete !== 1) return;
        const eventName = `${object}:get-items-list-data`;
        const handler = (payload) => {
            socket.off(eventName, handler);

            const list =
                (payload && Array.isArray(payload.data) && payload.data)
                || (Array.isArray(payload) ? payload : [])
                || (Array.isArray(payload?.[1]) ? payload[1] : []);

            const currentId = props.singleArgs?.id || props.singleArgs?.details?.id;
            const candidates = (list || []).filter((row) => Number(row.id) !== 0 && Number(row.id) !== Number(currentId));
            const nextId = candidates?.[0]?.id;
            setAfterDeletePath(nextId ? `/certificates/${nextId}/dashboard` : "/certificates");
        };
        socket.on(eventName, handler);
        socket.emit(`${object}:get-items-list`, {
            token: user.token,
            simple_search_text: "",
            order_by: "domain_name",
            order_rule: "asc",
        });
        return () => socket.off(eventName, handler);
    }, [response?.delete, props.singleArgs?.delete, user.token]);

    const formatStatus = (data) => <BadgeStatusView status={data['status']} lang={lang} />;

    const formatCertificate = (data) => {
        const issuedBy = data['issued_by'] || t.unknown;

        // dates: show as MM/DD/YYYY for consistency with your original
        const toUS = (s) => {
            if (!s) return 'N/A';
            const d = new Date(s);
            if (isNaN(d)) return t.invalidDate;
            return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        };

        const dateFrom = toUS(data['date_from']);
        const dateTo = toUS(data['date_to']);

        if (String(data['status']) === "0") return <>{t.notAvailable}</>;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{issuedBy}</span>
                <span style={{ fontSize: 12, color: '#6c757d' }}>
          {t.dateRange(dateFrom, dateTo)}
        </span>
            </div>
        );
    };

    const getExpirationMeta = (data) => {
        const now = moment();
        const expiration = moment(data['date_to']);
        const diff = moment.duration(expiration.diff(now));

        let color = '#f20000';
        let daysLeftText = t.expired;

        if (diff.asMilliseconds() > 0) {
            const days = Math.floor(diff.asDays());
            const hours = diff.hours();

            if (days <= 1) color = '#f20000';
            else if (days <= 10) color = '#ff9a44';
            else color = '#4CAF50';

            const dLbl = t.dayLbl(days);
            const hLbl = t.hourLbl(hours);
            daysLeftText = `${days} ${dLbl}${hours > 0 ? ` ${hours} ${hLbl}` : ''}`;
        }

        if (String(data['status']) === "0") {
            daysLeftText = t.notAvailable;
            color = '#d3d3d3';
        }

        return { color, text: daysLeftText };
    };

    const formatDomainAndDaysLeft = useCallback((data) => {
        const domainName = data['domain_name'] || t.unknown;
        const { text: daysLeftText, color } = getExpirationMeta(data);
        const certUrl = `/certificates/${data.id}/dashboard`;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Link
                    style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}
                    to={certUrl}
                >
                    {domainName}
                </Link>
                <span style={{ fontSize: 12, fontWeight: 'bold', color }}>
                    <span style={{ color: '#333', fontWeight: 'normal' }}>{t.daysLeftLabel} </span>
                    {daysLeftText}
                </span>
            </div>
        );
    }, [t]);

    const columns = useMemo(() => ([
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colDomainName, key: "domain_name", format: formatDomainAndDaysLeft },
        { label: t.colIssuedBy, key: "issued_by", format: formatCertificate },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ]), [t, formatDomainAndDaysLeft]);

    const renderCertificateCard = (item, helpers) => {
        const { text: daysLeftText, color } = getExpirationMeta(item);
        const contactGroup = item.contacts_group_name || t.unknown;
        const actionsList = helpers.actions || [];
        const handleAction = (actionName) => helpers.tableAction?.(actionName, item);

        return (
            <Link to={`/certificates/${item.id}/dashboard`} className="text-decoration-none text-dark">
                <div className="card h-100 shadow-sm border">
                    <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>{formatStatus(item)}</div>
                        <span
                            className="badge rounded-pill"
                            style={{ backgroundColor: color, color: "#fff" }}
                        >
                            {daysLeftText}
                        </span>
                    </div>

                    <div className="mb-3">
                        <div className="fw-semibold text-dark" style={{ fontSize: 22 }}>{item.domain_name || formatDomainAndDaysLeft(item)}</div>
                    </div>

                    <div className="mb-3">
                        {formatCertificate(item)}
                    </div>

                    <div className="text-muted small mb-3">
                        {t.colContactGroup}: <span className="fw-semibold text-dark">{contactGroup}</span>
                    </div>

                    {actionsList.length > 0 && (
                        <div className="mt-auto d-flex gap-2 justify-content-end">
                            {actionsList.map((action) => (
                                <button
                                    key={action.name}
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    title={action.label}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAction(action.name);
                                    }}
                                >
                                    <i className={action.icon || "ti ti-dots"}></i>
                                </button>
                            ))}
                        </div>
                    )}
                    </div>
                </div>
            </Link>
        );
    };

    const showField = (data, field) => {
        const key = field.key;
        const updated = { ...field };
        if ((key === "basic_auth_username" || key === "basic_auth_password") && String(data['basic_auth']) === "0") {
            updated.readonly = true;
        }
        return updated;
    };

    const fields = {
        add: [
            {
                type: "tab",
                name: t.tabMain,
                showField,
                fields: [
                    [
                        { label: t.fieldDomainName, key: "domain_name", default_value: "", showLabel: "top", readonly: false },
                        { label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" },
                    ],
                ],
            }
        ],
        edit: [
            {
                type: "tab",
                name: t.tabMain,
                showField,
                fields: [
                    [
                        { label: t.fieldDomainName, key: "domain_name", default_value: "", showLabel: "top", readonly: true },
                        { label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" },
                    ],
                ],
            }
        ],
    };

    const actions = canManageCertificates ? [
        { name: "edit", label: t.edit, controller: "crud", icon: "ti ti-edit" },
        { name: "delete", label: t.delete, controller: "crud", icon: "ti ti-trash" },
    ] : [];

    const expired = user?.expired ?? 1;
    const disabled = !canManageCertificates
        ? { add: { disable: true, title: t.memberViewOnly } }
        : (expired
            ? { add: { disable: true, title: t.planExpired } }
            : {
                add: {
                    disable: (totalData.certificates - totalData.used_certificates) < 1,
                    title: t.noResources,
                }
            });

    return (
        <>
            <BasicCRUD
                singleArgs={props?.singleArgs ?? false}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                disable={disabled}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                afterDeleteRedirect={afterDeletePath}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'domain_name',
                }}
                viewModes={["table", "blocks"]}
                renderItem={renderCertificateCard}
                cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
            >
                {!props.singleArgs && (
                    <ProgressBar lang={lang} used={totalData.used_certificates} allowed={totalData.certificates} />
                )}

                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default Certificates;
